// src/hooks/useZenohSubscription.ts
// Подписка на ключ Zenoh на время жизни компонента. Обработчик можно менять
// между рендерами — переподписки не будет (хранится в ref).
import { useEffect, useRef } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';

export function useZenohSubscription(
  session: zenoh.Session | null,
  key: string | null,
  onSample: (sample: zenoh.Sample) => void,
) {
  const handlerRef = useRef(onSample);
  handlerRef.current = onSample;

  useEffect(() => {
    if (!session || !key) return;
    let cancelled = false;
    let subscriber: zenoh.Subscriber | null = null;
    session
      .declareSubscriber(key, { handler: (sample: zenoh.Sample) => { if (!cancelled) handlerRef.current(sample); } })
      .then(sub => {
        if (cancelled) sub.undeclare().catch(() => {});
        else subscriber = sub;
      })
      .catch(err => console.warn(`subscribe ${key} failed`, err));
    return () => {
      cancelled = true;
      subscriber?.undeclare().catch(err => console.warn(`undeclare ${key} failed`, err));
    };
  }, [session, key]);
}

// То же для JSON-сообщений: битые сообщения пропускаются.
export function useZenohJson<T>(
  session: zenoh.Session | null,
  key: string | null,
  onMessage: (msg: T) => void,
) {
  useZenohSubscription(session, key, sample => {
    try {
      onMessage(JSON.parse(sample.payload().toString()) as T);
    } catch {
      /* не JSON */
    }
  });
}

export async function putJson(session: zenoh.Session | null, key: string, msg: unknown): Promise<boolean> {
  if (!session) return false;
  try {
    await session.put(key, JSON.stringify(msg));
    return true;
  } catch (err) {
    console.warn(`put ${key} failed`, err);
    return false;
  }
}

// Ответ на запрос (get) одним куском байт; ошибка — текстом из ответа
export async function getBytes(session: zenoh.Session, key: string, noReply = 'робот не ответил'): Promise<Uint8Array> {
  const rx = await session.get(key);
  if (!rx) throw new Error('запрос не отправлен');
  for await (const reply of rx) {
    const r = reply.result();
    if (r instanceof zenoh.Sample) return r.payload().toBytes();
    throw new Error(r.payload().toString());
  }
  throw new Error(noReply);
}
